import { ChangeEvent, useEffect, useRef } from 'react';
import classNames from 'classnames';
import BaseField, { BaseFieldProps, FieldState, FieldType } from '../Field';
import Icon, { IconType } from '../../icon/Icon';
import FieldLabel from '../FieldLabel';

interface CheckboxFieldProps extends BaseFieldProps {
  checked?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  indeterminate?: boolean;
  required?: boolean;
  label?: string;
  hideLabel?: boolean;
  tooltip?: string;
  amount?: string;
}

const CheckboxField = ({
  id,
  checked,
  onChange,
  value,
  state,
  indeterminate = false,
  required = false,
  label,
  hideLabel = false,
  tooltip,
  amount,
  ...props
}: CheckboxFieldProps) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <BaseField
      id={id}
      state={state}
      extraClasses="a-form-field--checkbox"
      {...props}
    >
      <div className="a-form-field__field">
        <input
          ref={checkboxRef}
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          value={value}
          className={classNames({
            'is-focus': state === FieldState.FOCUS,
            'is-focus-key': state === FieldState.FOCUS_KEYBOARD,
          })}
          aria-required={required}
          aria-invalid={state === FieldState.INVALID}
          disabled={state === FieldState.DISABLED}
          required={required}
          aria-describedby={
            props.description || props.errorMessage
              ? `${id}-message`
              : undefined
          }
        />
        {label && (
          <FieldLabel
            fieldType={FieldType.CHECKBOX}
            id={id}
            label={label}
            required={required}
            tooltip={tooltip}
            hideLabel={hideLabel}
            amount={amount}
          />
        )}
        <Icon iconType={IconType.Available} className="a-form-field__icon" />
        {indeterminate && (
          <Icon
            iconType={IconType.CheckMinus}
            className="a-form-field__icon a-form-field__icon--indeterminate"
          />
        )}
      </div>
    </BaseField>
  );
};

export default CheckboxField;
